import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Users, Plus, LogIn, Copy, Trash2, LogOut, Crown, ArrowRight } from "lucide-react";

export default function Classes() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/classes/me");
      setClasses(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/classes", { name });
      toast.success(`Class created — share code ${data.code}`);
      setName("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const join = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/classes/join", { code: code.trim().toUpperCase() });
      toast.success(`Joined ${data.name}`);
      setCode("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid code");
    } finally { setBusy(false); }
  };

  const copyCode = (c) => {
    navigator.clipboard?.writeText(c);
    toast.success("Code copied");
  };

  const leaveOrDelete = async (klass) => {
    const isCoach = klass.is_coach;
    const q = isCoach ? `Delete class "${klass.name}" for all members?` : `Leave class "${klass.name}"?`;
    if (!window.confirm(q)) return;
    await api.delete(`/classes/${klass.id}`);
    toast.success(isCoach ? "Class deleted" : "You left the class");
    load();
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">Classes</div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight flex items-center gap-4" data-testid="classes-title">
            <Users className="w-10 h-10" /> Class Groups
          </h1>
          <p className="mt-3 font-mono text-muted-foreground max-w-2xl">
            Create a private class for your batch, or join one your coach shared with you. Each class has its own leaderboard.
          </p>
        </div>

        {/* Create + Join forms */}
        <div className="grid md:grid-cols-2 gap-4 mb-12">
          <form onSubmit={create} className="border-2 border-border p-6 space-y-4" data-testid="class-create-form">
            <div className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              <div className="text-xs uppercase tracking-[0.3em] font-bold">Create a class (be the coach)</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold">Class name</Label>
              <Input
                required
                minLength={1}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Batch 2026 · Delhi Stenographers"
                className="mt-2 rounded-lg h-11 font-mono border-border"
                data-testid="class-create-name"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full rounded-lg h-11 bg-brand-gold hover:bg-brand-gold/90 text-white text-xs uppercase tracking-[0.2em] font-bold"
              data-testid="class-create-submit"
            >
              {busy ? "Creating…" : "Create class"}
            </Button>
          </form>

          <form onSubmit={join} className="border-2 border-border p-6 space-y-4" data-testid="class-join-form">
            <div className="flex items-center gap-2">
              <LogIn className="w-4 h-4" />
              <div className="text-xs uppercase tracking-[0.3em] font-bold">Join with a code</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-bold">Class code</Label>
              <Input
                required
                minLength={4}
                maxLength={12}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. 8S4ICB"
                className="mt-2 rounded-lg h-11 font-mono uppercase tracking-widest border-border"
                data-testid="class-join-code"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              variant="outline"
              className="w-full rounded-lg h-11 border-border text-xs uppercase tracking-[0.2em] font-bold"
              data-testid="class-join-submit"
            >
              {busy ? "Joining…" : "Join class"}
            </Button>
          </form>
        </div>

        {/* Class list */}
        <h2 className="text-2xl font-black tracking-tight mb-4">Your classes</h2>
        {loading ? (
          <div className="font-mono text-muted-foreground">Loading…</div>
        ) : classes.length === 0 ? (
          <div className="border-2 border-dashed border-border p-10 text-center font-mono text-muted-foreground" data-testid="classes-empty">
            You haven't created or joined any class yet.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="classes-list">
            {classes.map((c) => (
              <div key={c.id} className="border border-border bg-card p-6 flex flex-col" data-testid={`class-card-${c.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground/70">
                      {c.is_coach ? "Coach" : "Member"} · {c.member_count} member{c.member_count === 1 ? "" : "s"}
                    </div>
                    <h3 className="text-xl font-black tracking-tight mt-1">{c.name}</h3>
                  </div>
                  {c.is_coach && <Crown className="w-5 h-5 text-yellow-500" />}
                </div>
                <div className="flex items-center gap-2 mb-6">
                  <div className="border border-border bg-secondary/60 px-3 py-2 font-mono text-lg tracking-widest font-bold">{c.code}</div>
                  <Button
                    onClick={() => copyCode(c.code)}
                    variant="outline"
                    size="sm"
                    className="rounded-lg border-border"
                    data-testid={`class-copy-code-${c.id}`}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="mt-auto flex items-center justify-between gap-2 flex-wrap">
                  <Button
                    asChild
                    className="rounded-lg h-10 bg-brand-gold hover:bg-brand-gold/90 text-white text-xs uppercase tracking-[0.2em] font-bold"
                    data-testid={`class-view-lb-${c.id}`}
                  >
                    <Link to={`/leaderboard?class=${c.id}`}>
                      Class leaderboard <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Link>
                  </Button>
                  <Button
                    onClick={() => leaveOrDelete(c)}
                    variant="outline"
                    size="sm"
                    className={`rounded-lg border-red-300 text-red-700 hover:bg-red-50 ${c.is_coach ? "" : ""}`}
                    data-testid={`class-leave-${c.id}`}
                  >
                    {c.is_coach ? <><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</> : <><LogOut className="w-3.5 h-3.5 mr-1" /> Leave</>}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
